import { FC, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Button,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useToast,
  Icon,
  Text,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Image
} from '@chakra-ui/react';
import { MdArrowBack, MdSave } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ADMIN_ROUTES } from '@bemap/utils';
import { useForm } from 'react-hook-form';
import { Panel, PageHeading } from '@bemap/ui';
import { createContent, Content } from '@/services/university-service';

interface VideoContentFormData {
  title: string;
  videoUrl: string;
  order: number;
  duration: number;
}

const CreateVideoContentPage: FC = () => {
  const navigate = useNavigate();
  const { courseId, moduleId } = useParams<{ courseId: string; moduleId: string }>();
  const toast = useToast();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState<string>('');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid }
  } = useForm<VideoContentFormData>({
    defaultValues: {
      title: '',
      videoUrl: '',
      order: 1,
      duration: 0
    }
  });

  const handleBack = () => {
    navigate(`${ADMIN_ROUTES.UNIVERSITY_COURSES}/${courseId}`);
  };

  const onSubmit = async (data: VideoContentFormData) => {
    setIsSubmitting(true);

    try {
      const contentData: Partial<Content> = {
        title: data.title,
        data: {
          videoUrl: data.videoUrl
        },
        type: 'video',
        order: data.order,
        duration: data.duration.toString(),
        moduleId: parseInt(moduleId!),
        completed: false
      };

      const response = await createContent(contentData);

      if (response.statusCode === 201 || response.statusCode === 200) {
        toast({
          title: t('university.content.video_created'),
          description: `${t('actions.save')}"${data.title}" ${t('university.content.video_created')}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        // Navigate back to the course
        navigate(`${ADMIN_ROUTES.UNIVERSITY_COURSES}/${courseId}`);
      } else {
        throw new Error(response.message || t('university.content.error_creating_video'));
      }
    } catch (error: any) {
      console.error('Error creating video content:', error);
      toast({
        title: t('university.content.error_creating_video'),
        description: error.message || t('general.error'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVideoUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setValue('videoUrl', url);

    // Try to extract thumbnail from YouTube URL
    if (url.includes('youtube.com/watch?v=') || url.includes('youtu.be/')) {
      const videoId = url.includes('youtube.com/watch?v=')
        ? url.split('v=')[1].split('&')[0]
        : url.split('youtu.be/')[1]?.split('?')[0];

      if (videoId) setThumbnailUrl(`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`);
    } else if (url.includes('vimeo.com/')) {
      const videoId = url.split('vimeo.com/')[1]?.split('/')[0];
      if (videoId) setThumbnailUrl('https://via.placeholder.com/480x360cccccc/666666?text=Vimeo+Video');
    } else {
      setThumbnailUrl('');
    }
  };

  const getVideoEmbedUrl = (url: string) => {
    if (url.includes('youtube.com/watch?v=')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
    } else if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
    } else if (url.includes('vimeo.com/')) {
      const videoId = url.split('vimeo.com/')[1]?.split('/')[0];
      return videoId ? `https://player.vimeo.com/video/${videoId}` : '';
    }
    return '';
  };

  return (
    <VStack align="stretch" spacing={6}>
      {/* Header */}
      <PageHeading title={t('actions.save')} actions={[
        {
          label: t('university.content.back_to_content_types'),
          onClick: handleBack,
          icon: <Icon as={MdArrowBack} />,
          variant: 'outline'
        }
      ]} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <VStack align="stretch" spacing={6}>
          {/* Video Content Form */}
          <Panel heading={t('university.content.video_information')} padding={6}>
            <VStack spacing={4} align="stretch">
              <FormControl isInvalid={!!errors.title} isRequired>
                <FormLabel>{t('university.content.title')}</FormLabel>
                <Input
                  {...register('title', {
                    required: t('university.validation.title_required'),
                    minLength: { value: 3, message: t('university.validation.title_min_length') }
                  })}
                  placeholder={t('university.placeholders.video_title')}
                />
                <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.videoUrl} isRequired>
                <FormLabel>{t('university.content.video_url')}</FormLabel>
                <Input
                  {...register('videoUrl', {
                    required: t('university.validation.video_url_required'),
                    pattern: {
                      value: /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|vimeo\.com\/)/,
                      message: t('university.validation.video_url_format')
                    }
                  })}
                  placeholder={t('university.placeholders.video_url')}
                  onChange={handleVideoUrlChange}
                />
                <FormErrorMessage>{errors.videoUrl?.message}</FormErrorMessage>
                <Text fontSize="sm" color="gray.600" mt={1}>
                  {t('university.content.supported_platforms')}
                </Text>
              </FormControl>

              <HStack spacing={4}>
                <FormControl>
                  <FormLabel>{t('university.content.order')}</FormLabel>
                  <NumberInput min={1} defaultValue={1}>
                    <NumberInputField {...register('order', { valueAsNumber: true })} />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>{t('university.content.video_duration')}</FormLabel>
                  <NumberInput min={1} defaultValue={0}>
                    <NumberInputField {...register('duration', { valueAsNumber: true })} />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text fontSize="sm" color="gray.600" mt={1}>
                    {t('university.content.minutes')}
                  </Text>
                </FormControl>
              </HStack>
            </VStack>
          </Panel>

          {/* Video Preview */}
          {watch('videoUrl') && (
            <Panel heading={t('university.content.video_preview')} padding={6}>
              <VStack spacing={4} align="stretch">
                {thumbnailUrl && (
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={2}>
                      {t('university.content.thumbnail')}
                    </Text>
                    <Image
                      src={thumbnailUrl}
                      alt="Video thumbnail"
                      maxH="200px"
                      borderRadius="md"
                      objectFit="cover"
                    />
                  </Box>
                )}

                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    {t('university.content.embed_preview')}
                  </Text>
                  <Box
                    position="relative"
                    paddingBottom="56.25% 16"
                    borderRadius="md"
                    overflow="hidden"
                    bg="gray.100"
                  >
                    <iframe
                      src={getVideoEmbedUrl(watch('videoUrl'))}
                      title="Video preview"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        border: 0
                      }}
                      allowFullScreen
                    />
                  </Box>
                </Box>
              </VStack>
            </Panel>
          )}

          {/* Action Buttons */}
          <HStack justify="space-between">
            <Button
              variant="outline"
              onClick={handleBack}
              isDisabled={isSubmitting}
            >
              {t('actions.cancel')}
            </Button>

            <HStack spacing={3}>
              <Button
                type="submit"
                colorScheme="blue"
                leftIcon={<Icon as={MdSave} />}
                isLoading={isSubmitting}
                loadingText={t('general.creating')}
                isDisabled={!isValid}
              >
                {t('actions.save')}
              </Button>
            </HStack>
          </HStack>
        </VStack>
      </form>
    </VStack>
  );
};

export default CreateVideoContentPage; 